import PropTypes from 'prop-types';
import React, { useContext, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { LoadingContext, M_StateActions, MTable } from 'src/M_Components';

//===============================================================================
// AddressConfig list page. Renders the Addresses table and opens the CRUD tab
// (Create/Edit/View) wiring the necessary global state.
//===============================================================================
export default function AddressConfig({
  roles,
  onCreateTab,
  toastMessages,
  selectedAddress,
  setSelectedAddress,
  refresh: externalRefresh,
  setRefresh: setExternalRefresh,
}) {
  const ctx = useContext(LoadingContext);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // When the parent page provides a refresh controller, use it; otherwise keep an internal one.
  const [internalRefresh, setInternalRefresh] = useState(false);
  const refresh = externalRefresh ?? internalRefresh;
  const setRefresh = setExternalRefresh ?? setInternalRefresh;

  // Keep the context menu clean (stable memoized value).
  const removeContextItems = useMemo(() => ['Print', 'Copy'], []);

  // CRUD route for this entity (must match AddressPage routes).
  const CRUD_ROUTE = '/home/addresses/addressConfigCrud';

  //===============================================================================
  // Open CRUD tab for the requested operation (Create/Edit/View),
  // pushing the required data into the global M_State store.
  //=   ========================================================================
  const openAddressCrud = useCallback(
    (operation, row) => {
      // Persist operation (read by the CRUD screen)
      dispatch(M_StateActions.addM_State({ key: 'operation', value: operation }));

      // For Edit/View we must carry the selected row
      if (['Edit', 'Copy', 'View'].includes(operation)) {
        if (!row) {
          toastMessages('error', '', t('SelectedLine'));
          return;
        }
        dispatch(M_StateActions.addM_State({ key: 'AddressLine', value: row }));
        // Keep local selection in sync if the parent page provided a setter
        setSelectedAddress?.(row);
      }

      // Build tab label by operation
      const tabLabel =
        operation === 'Create'
          ? t('LabelNewAddress')
          : operation === 'View'
          ? `${t('LabelViewAddress')} "${row?.Name ?? ''}"`
          : `${t('LabelEditAddress')} "${row?.Name ?? ''}"`;

      onCreateTab(CRUD_ROUTE, tabLabel);
    },
    [dispatch, onCreateTab, setSelectedAddress, t, toastMessages]
  );

  //===============================================================================
  // Provide custom handlers to replace default MTable actions.
  //===============================================================================
  const replaceFunctions = useMemo(
    () =>
      new Map([
        ['View', (row) => openAddressCrud('View', row)],
        ['Create', () => openAddressCrud('Create')],
        ['Edit', (row) => openAddressCrud('Edit', row)],
      ]),
    [openAddressCrud]
  );

  return (
    <div className="divGlobalSettings">
      <div className="subTitleSettings">
        <div>
          <div className="titleSettings" style={{ color: ctx.secondaryColor }}>
            {t('Addresses')}
          </div>
          <div className="labelSubtitleSettings">{t('InfoAddresses')}</div>
        </div>
      </div>

      <MTable
        // Dynamic table for Addresses
        filtersOnTable
        entityName={'Addresses'}
        roleName={'Accounts'}
        rolePermissions={roles?.map((r) => r.Value)}
        removeContextItems={removeContextItems}
        refresh={refresh}
        setRefresh={setRefresh}
        useSessionStorage={false}
        replaceFunctions={replaceFunctions}
      />
    </div>
  );
}

AddressConfig.propTypes = {
  roles: PropTypes.array,
  onCreateTab: PropTypes.func,
  toastMessages: PropTypes.func,
  selectedAddress: PropTypes.object,
  setSelectedAddress: PropTypes.func,
  refresh: PropTypes.bool,
  setRefresh: PropTypes.func,
};
